import Component from 'flarum/common/Component';
import m from 'mithril';
/**
 * Response time over the selected range, drawn as inline SVG — no charting
 * library, so it stays light and takes its colors from the Flarum theme.
 *
 * The SVG is drawn at the container's real pixel width rather than scaled from
 * a fixed viewBox. Scaling a viewBox to fit stretches everything inside it,
 * axis text included, which is what made an earlier version look smeared.
 */
export default class ResponseChart extends Component {
  width = 0;
  /** Index of the bucket under the pointer, or null when the pointer is away. */
  hover = null;
  observer = null;
  onWindowResize = null;
  oncreate(vnode) {
    super.oncreate(vnode);
    const el = vnode.dom;
    this.measure(el);

    // A window resize doesn't run a Mithril redraw, so measuring in onupdate
    // alone left the chart at whatever width it was first drawn at. Watch the
    // container itself, which also catches a sidebar opening or the panel
    // reflowing at a breakpoint — neither of which is a window resize.
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measure(el));
      this.observer.observe(el);
    } else {
      this.onWindowResize = () => this.measure(el);
      window.addEventListener('resize', this.onWindowResize);
    }
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.measure(vnode.dom);
  }
  onremove(vnode) {
    this.observer?.disconnect();
    this.observer = null;
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize);
      this.onWindowResize = null;
    }
    super.onremove(vnode);
  }

  /**
   * Redraw only on a real width change, so this can't loop — the observer
   * fires on height changes too, and redrawing changes the plot's height.
   * The redraw is deferred out of the observer callback, which otherwise
   * mutates the DOM mid-layout and trips the browser's loop guard.
   */
  measure(el) {
    const w = el.clientWidth;
    if (!w || Math.abs(w - this.width) <= 1) return;
    this.width = w;
    requestAnimationFrame(() => m.redraw());
  }
  view() {
    const W0 = this.width || 720;
    const buckets = this.coalesce(this.attrs.buckets || [], W0);
    const withData = buckets.filter(b => b.avgMs != null);
    if (!withData.length) {
      return m("div", {
        className: "LrChart"
      }, m("div", {
        className: "LrChart-empty"
      }, "No response data in this range yet."));
    }
    const W = W0;
    // Narrow screens get a shorter plot, a tighter y-axis gutter and fewer
    // labels — five timestamps at 11px collide well before a phone's width.
    const narrow = W < 520;
    const H = this.attrs.height ?? (narrow ? 150 : 180);
    const padL = narrow ? 34 : 48;
    const padR = narrow ? 8 : 14;
    const padT = 14;
    const padB = 26;
    const plotW = Math.max(1, W - padL - padR);
    const plotH = Math.max(1, H - padT - padB);

    // The ceiling has to clear the band, not just the line: a coalesced
    // point's maxMs can sit well above its mean, and an axis sized to the
    // means alone clips the band at the top of the plot.
    const niceMax = this.niceCeil(Math.max(1, ...withData.map(b => Math.max(b.avgMs, b.maxMs ?? 0))));
    const x = i => padL + (buckets.length < 2 ? plotW / 2 : i / (buckets.length - 1) * plotW);
    const y = ms => padT + plotH - ms / niceMax * plotH;
    const colW = Math.max(2, plotW / buckets.length);

    // Runs of buckets that have data, so a real absence of data breaks the
    // line instead of being drawn as a plunge to zero.
    //
    // Brief gaps are bridged rather than broken. A bucket is about one check
    // wide, so a single late or missed check — a restart, a slow tick — would
    // otherwise shred an otherwise healthy line into fragments. Anything
    // longer than BRIDGE buckets is a genuine hole and still breaks.
    const BRIDGE = 2;
    const runs = [];
    let run = [];
    let missing = 0;
    buckets.forEach((b, i) => {
      if (b.avgMs == null) {
        if (run.length) missing++;
        if (missing > BRIDGE) {
          if (run.length) runs.push(run);
          run = [];
          missing = 0;
        }
        return;
      }
      missing = 0;
      run.push(i);
    });
    if (run.length) runs.push(run);
    const pathOf = r => r.map((i, n) => `${n ? 'L' : 'M'}${x(i).toFixed(1)},${y(buckets[i].avgMs).toFixed(1)}`).join('');
    // The spread of the raw checks inside each drawn point, as a soft band
    // behind the line: forward along the maxima, back along the minima.
    const bands = runs.filter(r => r.length > 1 && r.some(i => buckets[i].maxMs != null)).map(r => {
      const top = r.map((i, n) => `${n ? 'L' : 'M'}${x(i).toFixed(1)},${y(buckets[i].maxMs ?? buckets[i].avgMs).toFixed(1)}`).join('');
      const back = [...r].reverse().map(i => `L${x(i).toFixed(1)},${y(buckets[i].minMs ?? buckets[i].avgMs).toFixed(1)}`).join('');
      return `${top}${back}Z`;
    });
    const lines = runs.filter(r => r.length > 1).map(pathOf);
    // A run of one can't be a line — draw it as a dot, so a monitor with a
    // single bucket of data still shows something.
    const dots = runs.filter(r => r.length === 1).map(r => r[0]);
    const areas = runs.filter(r => r.length > 1).map(r => `${pathOf(r)}L${x(r[r.length - 1]).toFixed(1)},${(padT + plotH).toFixed(1)}L${x(r[0]).toFixed(1)},${(padT + plotH).toFixed(1)}Z`);
    const spanSeconds = this.span(buckets);
    const labelOf = b => this.labelFor(b, spanSeconds);
    const lastIndex = buckets.lastIndexOf(withData[withData.length - 1]);
    const labelCount = Math.min(narrow ? 2 : W < 760 ? 3 : 5, buckets.length);
    const labelIdx = Array.from({
      length: labelCount
    }, (_, n) => Math.round(n / Math.max(1, labelCount - 1) * (buckets.length - 1)));
    const hovered = this.hover != null && this.hover >= 0 && this.hover < buckets.length ? this.hover : null;
    return m("div", {
      className: "LrChart"
    }, hovered != null ? this.readout(buckets[hovered], labelOf(buckets[hovered]), x(hovered), W) : null, m("svg", {
      width: W,
      height: H,
      viewBox: `0 0 ${W} ${H}`,
      role: "img",
      "aria-label": "Response time over the selected range"
    }, (narrow ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]).map(f => {
      const gy = padT + plotH - f * plotH;
      return m("g", null, m("line", {
        className: "LrChart-grid",
        x1: padL,
        y1: gy.toFixed(1),
        x2: W - padR,
        y2: gy.toFixed(1)
      }), m("text", {
        className: "LrChart-axis",
        x: padL - 6,
        y: (gy + 3.5).toFixed(1),
        "text-anchor": "end"
      }, Math.round(niceMax * f)));
    }), buckets.map((b, i) => b.downSeconds > 0 ? m("rect", {
      className: "LrChart-down",
      x: (x(i) - colW / 2).toFixed(1),
      y: padT,
      width: colW.toFixed(1),
      height: plotH,
      opacity: Math.min(0.85, 0.3 + b.downSeconds / Math.max(1, b.spanSeconds))
    }, m("title", null, `${labelOf(b)} · ${this.dur(b.downSeconds)} down`)) : null), bands.map(d => m("path", {
      className: "LrChart-band",
      d: d
    })), areas.map(d => m("path", {
      className: "LrChart-area",
      d: d
    })), m("g", {
      className: "LrChart-lines"
    }, lines.map(d => m("path", {
      className: "LrChart-line",
      d: d
    }))), dots.map(i => m("circle", {
      className: "LrChart-dot",
      cx: x(i).toFixed(1),
      cy: y(buckets[i].avgMs).toFixed(1),
      r: "3.5"
    })), lines.length ? m("g", {
      className: "LrChart-live"
    }, m("circle", {
      className: "LrChart-halo",
      cx: x(lastIndex).toFixed(1),
      cy: y(withData[withData.length - 1].avgMs).toFixed(1),
      r: "4"
    }), m("circle", {
      className: "LrChart-dot",
      cx: x(lastIndex).toFixed(1),
      cy: y(withData[withData.length - 1].avgMs).toFixed(1),
      r: "3.5"
    })) : null, labelIdx.map((i, n) => m("text", {
      className: "LrChart-axis",
      x: x(i).toFixed(1),
      y: H - 8,
      "text-anchor": n === 0 ? 'start' : n === labelIdx.length - 1 ? 'end' : 'middle'
    }, buckets[i] ? labelOf(buckets[i]) : '')), hovered != null ? m("g", {
      className: "LrChart-cursor"
    }, m("line", {
      x1: x(hovered).toFixed(1),
      y1: padT,
      x2: x(hovered).toFixed(1),
      y2: (padT + plotH).toFixed(1)
    }), buckets[hovered].avgMs != null ? m("circle", {
      className: "LrChart-cursorDot",
      cx: x(hovered).toFixed(1),
      cy: y(buckets[hovered].avgMs).toFixed(1),
      r: "4.5"
    }) : null) : null, m("rect", {
      x: padL,
      y: padT,
      width: plotW.toFixed(1),
      height: plotH.toFixed(1),
      fill: "transparent",
      onmousemove: e => this.track(e),
      onmouseleave: () => this.leave(),
      ontouchmove: e => this.track(e),
      ontouchend: () => this.leave()
    })));
  }

  /**
   * Snap to the bucket nearest the pointer.
   *
   * Nearest rather than "the one under it": at thirty days a bucket is a couple
   * of pixels wide, and requiring an exact hit makes the chart feel broken.
   */
  track(e) {
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const box = svg.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
    if (clientX == null) return;
    const W = this.width || box.width;
    const buckets = this.coalesce(this.attrs.buckets || [], W);
    if (buckets.length < 1) return;
    const narrow = W < 520;
    const padL = narrow ? 34 : 48;
    const padR = narrow ? 8 : 14;
    const plotW = Math.max(1, W - padL - padR);
    const rel = clientX - box.left - padL;
    const frac = Math.max(0, Math.min(1, rel / plotW));
    const i = Math.round(frac * (buckets.length - 1));
    if (i !== this.hover) {
      this.hover = i;
      m.redraw();
    }
  }

  /**
   * Merge neighbouring buckets until each drawn point has a few pixels to
   * itself. A day of five-minute checks is ~288 buckets; on a phone's 300px
   * plot that is one point per pixel, and the line becomes a solid smear of
   * jitter that says nothing. Merged points carry the checks-weighted mean as
   * the line and the members' min–max as a band, so the jitter is still
   * visible as a spread rather than as noise. Purely presentational: the
   * server's buckets are untouched and the same method is used by hover.
   */
  coalesce(buckets, W) {
    const narrow = W < 520;
    const plotW = Math.max(1, W - (narrow ? 34 : 48) - (narrow ? 8 : 14));
    const PX_PER_POINT = 5;
    const group = Math.ceil(buckets.length / Math.max(1, Math.floor(plotW / PX_PER_POINT)));
    if (group <= 1) return buckets;
    const out = [];
    for (let i = 0; i < buckets.length; i += group) {
      const members = buckets.slice(i, i + group);
      const withData = members.filter(b => b.avgMs != null);
      const checks = members.reduce((n, b) => n + (b.checks || 0), 0);
      // Weighted by checks, so a member with more samples counts for more;
      // falls back to a plain mean when no member reports a count.
      const weight = withData.reduce((n, b) => n + (b.checks || 0), 0);
      const avg = withData.length ? weight > 0 ? withData.reduce((n, b) => n + b.avgMs * (b.checks || 0), 0) / weight : withData.reduce((n, b) => n + b.avgMs, 0) / withData.length : null;
      const last = members[members.length - 1];
      out.push({
        at: members[0].at,
        label: members.length > 1 ? `${members[0].label} – ${last.label}` : members[0].label,
        checks,
        avgMs: avg == null ? null : Math.round(avg),
        failures: members.reduce((n, b) => n + (b.failures || 0), 0),
        downSeconds: members.reduce((n, b) => n + (b.downSeconds || 0), 0),
        spanSeconds: members.reduce((n, b) => n + (b.spanSeconds || 0), 0),
        minMs: withData.length ? Math.min(...withData.map(b => b.avgMs)) : null,
        maxMs: withData.length ? Math.max(...withData.map(b => b.avgMs)) : null
      });
    }
    return out;
  }
  leave() {
    if (this.hover !== null) {
      this.hover = null;
      m.redraw();
    }
  }

  /**
   * The reading, as HTML above the plot rather than inside the SVG — text in a
   * box is far easier to lay out and read than tspans, and it can never be
   * clipped by the viewBox.
   */
  readout(b, label, cx, W) {
    // Keep the box inside the chart: it follows the cursor until it would
    // overhang, then stops.
    const half = 90;
    const left = Math.max(half, Math.min(W - half, cx));
    return m("div", {
      className: "LrChart-readout",
      style: `left:${left}px`
    }, m("span", {
      className: "LrChart-readoutTime"
    }, label), m("span", {
      className: "LrChart-readoutMs"
    }, b.avgMs != null ? `${b.avgMs} ms` : 'no checks', b.minMs != null && b.maxMs != null && b.maxMs !== b.minMs ? m("small", null, " ", b.minMs, "\u2013", b.maxMs) : null), m("span", {
      className: "LrChart-readoutMeta"
    }, b.checks ? `${b.checks} check${b.checks === 1 ? '' : 's'}` : 'no checks', b.failures ? ` · ${b.failures} failed` : '', b.downSeconds ? ` · ${this.dur(b.downSeconds)} down` : ''));
  }

  /**
   * Axis and tooltip labels in the VIEWER's timezone.
   *
   * The server preformats a `label` in its own zone, which puts a Chicago
   * reader's 2pm spike at 20:00 on the axis. The bucket also carries `at` as an
   * absolute instant, so the browser can label it on the reader's own clock.
   * The server's string stays the fallback for anything Intl can't parse.
   */
  labelFor(b, spanSeconds) {
    if (!b.at) return b.label;
    const d = new Date(b.at);
    if (isNaN(d.getTime())) return b.label;

    // The same three steps the server picks between, so the axis reads the way
    // it always did — just on the right clock.
    const opts = spanSeconds <= 86400 ? {
      hour: '2-digit',
      minute: '2-digit'
    } : spanSeconds <= 604800 ? {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    } : {
      month: 'short',
      day: 'numeric'
    };
    try {
      return new Intl.DateTimeFormat(undefined, opts).format(d);
    } catch (e) {
      return b.label;
    }
  }

  /** How much time the drawn buckets actually cover. */
  span(buckets) {
    if (buckets.length < 2) return buckets[0]?.spanSeconds ?? 3600;
    const a = new Date(buckets[0].at).getTime();
    const z = new Date(buckets[buckets.length - 1].at).getTime();
    if (isNaN(a) || isNaN(z)) return 86400;
    return (z - a) / 1000 + (buckets[buckets.length - 1].spanSeconds || 0);
  }

  /**
   * Axis ceiling. Rounds up to a readable number, then tries half and
   * three-quarter steps so a 640ms peak tops out at 750 rather than 1000 and
   * the line uses the full height of the plot.
   */
  niceCeil(v) {
    const pow = Math.pow(10, Math.floor(Math.log10(v)));
    const n = Math.ceil(v / pow) * pow;
    if (n / 2 >= v) return n / 2;
    if (n * 0.75 >= v) return n * 0.75;
    return n;
  }
  dur(s) {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${(s / 3600).toFixed(1)}h`;
  }
}
flarum.reg.add('linkrobins-lr-monitoring', 'components/ResponseChart', ResponseChart);